import type { CoachingEndpointKey } from "../services/coaching-contracts";

export const coachingQueryKeys = {
  root: ["coaching"] as const,
  profile: () => [...coachingQueryKeys.root, "profile"] as const,
  dashboard: (role: string) => [...coachingQueryKeys.root, "dashboard", role] as const,
  list: (family: CoachingEndpointKey, query: Record<string, unknown> = {}) =>
    [...coachingQueryKeys.root, "list", family, stableQuery(query)] as const,
  detail: (family: CoachingEndpointKey, id: string) => [...coachingQueryKeys.root, "detail", family, id] as const,
  companyDetail: (id: string) => coachingQueryKeys.detail("companies.detail", id),
  programDetail: (id: string) => coachingQueryKeys.detail("programs.detail", id),
  cohortDetail: (id: string) => coachingQueryKeys.detail("cohorts.detail", id),
  participantDetail: (id: string) => coachingQueryKeys.detail("participants.list", id),
  reportDetail: (id: string) => coachingQueryKeys.detail("reports.list", id),
  resourceDetail: (id: string) => coachingQueryKeys.detail("resources.detail", id),
  sessionDetail: (id: string) => coachingQueryKeys.detail("sessions.detail", id),
  userDetail: (id: string) => coachingQueryKeys.detail("users.list", id),
  recordScopeRoot: () => [...coachingQueryKeys.root, "record-scope"] as const,
  // Phase 4 org analytics rollups (analytics spec §4.4).
  analytics: {
    root: () => [...coachingQueryKeys.root, "analytics"] as const,
    orgSummary: (query: Record<string, unknown> = {}) =>
      [...coachingQueryKeys.root, "analytics", "org-summary", stableQuery(query)] as const,
    timeSeries: (query: Record<string, unknown> = {}) =>
      [...coachingQueryKeys.root, "analytics", "time-series", stableQuery(query)] as const,
    reportStatus: (query: Record<string, unknown> = {}) =>
      [...coachingQueryKeys.root, "analytics", "report-status", stableQuery(query)] as const
  },
  recordScope: (role: string, scopeKind: string, recordId: string) =>
    [...coachingQueryKeys.root, "record-scope", role, scopeKind, recordId] as const,
  scopedDiscovery: (role: string, kind: string, query: Record<string, unknown> = {}) =>
    [...coachingQueryKeys.root, "scoped-discovery", role, kind, stableQuery(query)] as const,
  // Readiness lane reads (settings / capstone-read / certificates).
  readiness: {
    thresholds: (query: Record<string, unknown> = {}) =>
      [...coachingQueryKeys.root, "readiness", "thresholds", stableQuery(query)] as const,
    certificates: (query: Record<string, unknown> = {}) =>
      [...coachingQueryKeys.root, "readiness", "certificates", stableQuery(query)] as const,
    capstoneForCohort: (cohortId: string) =>
      [...coachingQueryKeys.root, "readiness", "capstone", "cohort", cohortId] as const,
    capstoneDetail: (submissionId: string) =>
      [...coachingQueryKeys.root, "readiness", "capstone", "detail", submissionId] as const,
    // FR-TOOL-001 SA-only Tool View diagnostics (gate/outbox/flags, no secrets).
    toolDiagnostics: (query: Record<string, unknown> = {}) =>
      [...coachingQueryKeys.root, "readiness", "tool-diagnostics", stableQuery(query)] as const
  },
  // Z6 Zoom-integration read surfaces (F1/F2/F4/F5/F8/F9/F10/F14).
  z6: {
    recordings: (sessionId: string) => [...coachingQueryKeys.root, "z6", "recordings", sessionId] as const,
    inviteLog: (sessionId: string) => [...coachingQueryKeys.root, "z6", "invite-log", sessionId] as const,
    postSessionEmailLog: (sessionId: string) => [...coachingQueryKeys.root, "z6", "post-session-email-log", sessionId] as const,
    progressWeekWise: (cohortId: string) => [...coachingQueryKeys.root, "z6", "progress-week-wise", cohortId] as const,
    attendance: (cohortId: string) => [...coachingQueryKeys.root, "z6", "attendance", cohortId] as const,
    assignments: (cohortId: string) => [...coachingQueryKeys.root, "z6", "assignments", cohortId] as const,
    useCases: (query: Record<string, unknown> = {}) => [...coachingQueryKeys.root, "z6", "use-cases", stableQuery(query)] as const,
    useCaseDetail: (useCaseId: string) => [...coachingQueryKeys.root, "z6", "use-case", useCaseId] as const
  }
};

/**
 * Participant session reads cache several own cohorts under one aggregate key
 * (`sessions.listForCohort` with `{ cohort_ids: "a,b" }`). Partial key matching cannot express
 * "contains this cohort", so writes that change a cohort's sessions use this predicate to reach
 * those aggregates as well as the per-cohort `{ cohort_id }` key.
 */
export function isAggregateSessionListContaining(queryKey: readonly unknown[], cohortId: string): boolean {
  const [root, kind, family, params] = queryKey;
  if (root !== coachingQueryKeys.root[0] || kind !== "list" || family !== "sessions.listForCohort") return false;
  const ids = (params as { cohort_ids?: unknown } | undefined)?.cohort_ids;
  return typeof ids === "string" && ids.split(",").includes(cohortId);
}

function stableQuery(query: Record<string, unknown>) {
  return Object.keys(query)
    .sort()
    .reduce<Record<string, unknown>>((accumulator, key) => {
      accumulator[key] = query[key];
      return accumulator;
    }, {});
}
